/**
 * Build Tracker — admin-only Type 1 Standard surface at /admin/builds.
 *
 * Day 99b.4 — last of the Day-99b arbi-dev ops-surface cluster; replaces the
 * Day-62a Placeholder. The Day-63b sentry pipeline "minus the triage half":
 * structurally mirrors ErrorLogViewer.tsx / AlgopixJobMonitor.tsx (themeQuartz
 * grid, AG Grid Infinite Row Model, FilterGroup pills, search, slide-in detail
 * panel, useDocumentTitle) and is READ-ONLY like AlgopixJobMonitor — a build
 * log, not a triage queue, so NO bulk-action bar, NO checkbox column, NO triage
 * RPC, NO nav badge.
 *
 * Datasource — PostgREST-direct on public.builds (append-only CI build log fed
 * by the receive-build-event Edge Function from GitHub workflow_run webhooks),
 * { count: 'exact' } + .range(...), ordered run_started_at DESC (the timeline).
 *
 * conclusion is GitHub's full set (success / failure / cancelled / timed_out /
 * action_required / neutral / skipped / stale): the badge maps success→success,
 * failure→danger, everything-else→neutral, with human-readable labels (TONE
 * §12). The Conclusion filter is exact-match All / Success / Failure for V1.
 *
 * NO nav badge (deferred): a build is a terminal fact, not a triageable item —
 * a failures count is either monotonic (never zeros) or non-eq (useBadgeCount
 * is eq-only). A future "main red/green" CI-health signal is the corrected
 * path, not a triage/ack model. See BACKLOG.
 */

import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
} from 'ag-grid-community';
import { Search, X, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { reportDatasourceFailure } from '../lib/agGridErrorTracker';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  conclusionTone,
  conclusionLabel,
  buildSearchOrFilter,
  type ConclusionTone,
} from '../lib/buildTracker';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Local types ───────────────────────────────────────────────────────────────

type ConclusionFilter = 'success' | 'failure' | null;
type BranchFilter = 'main' | null;
type TimeFilter = '7d' | '30d' | 'all';

interface BuildRow {
  id: string;
  run_id: string;
  run_attempt: number;
  commit_sha: string;
  commit_message: string | null;
  head_branch: string | null;
  conclusion: string;
  actor: string | null;
  html_url: string | null;
  run_started_at: string;
  received_at: string;
  source: string;
  payload: Record<string, unknown> | null;
}

// ── Filter constants ──────────────────────────────────────────────────────────

const CONCLUSION_FILTERS: ReadonlyArray<{ id: ConclusionFilter; label: string }> = [
  { id: null, label: 'All' },
  { id: 'success', label: 'Success' },
  { id: 'failure', label: 'Failure' },
] as const;

const BRANCH_FILTERS: ReadonlyArray<{ id: BranchFilter; label: string }> = [
  { id: null, label: 'All' },
  { id: 'main', label: 'main' },
] as const;

// Default 'all' (not '7d'): builds are a low-volume append-only log, and an
// 'all' default keeps hasFilters=false on a fresh empty table so the first-time
// empty state (not the filter-empty one) shows correctly.
const TIME_FILTERS: ReadonlyArray<{ id: TimeFilter; label: string }> = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: 'all', label: 'All' },
] as const;

function timeFilterCutoff(t: TimeFilter): string | null {
  if (t === 'all') return null;
  const ms = t === '7d' ? 604_800_000 : 2_592_000_000;
  return new Date(Date.now() - ms).toISOString();
}

// ── Conclusion badge ───────────────────────────────────────────────────────────
// GitHub conclusion is one of success / failure / cancelled / timed_out /
// action_required / neutral / skipped / stale. success→success, failure→danger,
// everything-else→neutral (DEFAULT branch) per UI_STANDARDS §10.

const TONE_CLASS: Record<ConclusionTone, string> = {
  success: 'bg-success/15 text-success',
  danger: 'bg-danger/15 text-danger',
  neutral: 'bg-surface-3 text-ink-muted',
};

function ConclusionBadge({ conclusion }: { conclusion: string }): JSX.Element {
  return (
    <span
      className={clsx(
        'px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap',
        TONE_CLASS[conclusionTone(conclusion)]
      )}
    >
      {conclusionLabel(conclusion)}
    </span>
  );
}

// ── Relative time (inline-replicated per ErrorLogViewer/Algopix precedent) ──────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Filter pill component (inline-replicated) ───────────────────────────────────

interface FilterGroupProps<T extends string | null> {
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (id: T) => void;
}

function FilterGroup<T extends string | null>({
  options,
  value,
  onChange,
}: FilterGroupProps<T>): JSX.Element {
  return (
    <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
      {options.map((f) => (
        <button
          key={f.id ?? 'all'}
          onClick={() => onChange(f.id)}
          aria-pressed={value === f.id}
          className={clsx(
            'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer whitespace-nowrap',
            value === f.id ? 'bg-surface-4 text-ink-bright' : 'text-ink-muted hover:text-ink-secondary'
          )}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// ── Detail panel (slide-in; read-only — no triage actions) ──────────────────────

function BuildDetailPanel({ row, onClose }: { row: BuildRow; onClose: () => void }): JSX.Element {
  const [showPayload, setShowPayload] = useState<boolean>(false);

  useEffect(() => {
    setShowPayload(false);
  }, [row.id]);

  return (
    <div className="w-96 flex-shrink-0 bg-surface-1 border-l border-border h-full overflow-y-auto pr-2.5">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <h3 className="text-sm font-semibold text-ink-bright truncate pr-2">
          {row.commit_message ?? '(no commit message)'}
        </h3>
        <button
          onClick={onClose}
          className="text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
          aria-label="Close panel"
        >
          <X size={16} />
        </button>
      </div>
      <div className="p-4 space-y-4">
        {/* Top metadata row */}
        <div className="flex items-center gap-2 flex-wrap">
          <ConclusionBadge conclusion={row.conclusion} />
          {row.head_branch && (
            <span className="text-2xs font-mono text-ink-muted px-1.5 py-0.5 rounded bg-surface-3">
              {row.head_branch}
            </span>
          )}
          <span className="text-2xs text-ink-muted px-1.5 py-0.5 rounded bg-surface-3">{row.source}</span>
        </div>

        <div>
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Commit</p>
          <p className="text-2xs font-mono text-ink-secondary break-all">{row.commit_sha}</p>
        </div>

        {row.commit_message && (
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Message</p>
            <p className="text-xs text-ink-primary whitespace-pre-wrap break-words">
              {row.commit_message}
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Branch</p>
            <p className="text-xs text-ink-primary break-words">{row.head_branch ?? '—'}</p>
          </div>
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Actor</p>
            <p className="text-xs text-ink-primary break-words">{row.actor ?? '—'}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Run</p>
            <p className="text-xs text-ink-primary break-words">
              #{row.run_id} · attempt {row.run_attempt}
            </p>
          </div>
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Conclusion</p>
            <p className="text-xs text-ink-primary">{conclusionLabel(row.conclusion)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Started</p>
            <p className="text-2xs font-mono text-ink-muted">{relativeTime(row.run_started_at)}</p>
          </div>
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Received</p>
            <p className="text-2xs font-mono text-ink-muted">{relativeTime(row.received_at)}</p>
          </div>
        </div>

        {/* View run on GitHub */}
        {row.html_url && (
          <div className="border-t border-border-subtle pt-3">
            <a
              href={row.html_url}
              target="_blank"
              rel="noopener noreferrer"
              className="arbi-btn-ghost text-xs w-full justify-center"
            >
              View run on GitHub <ExternalLink size={11} />
            </a>
          </div>
        )}

        {/* Raw payload (collapsed by default — the full workflow_run object) */}
        <div className="border-t border-border-subtle pt-3">
          <button
            onClick={() => setShowPayload((s) => !s)}
            className="text-2xs text-accent hover:underline"
          >
            {showPayload ? 'Hide raw payload' : 'Show raw payload'}
          </button>
          {showPayload && (
            <pre className="mt-2 text-2xs font-mono text-ink-secondary bg-surface-2 rounded p-2 whitespace-pre-wrap break-all max-h-80 overflow-y-auto">
              {JSON.stringify(row.payload, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function BuildTracker(): JSX.Element {
  useDocumentTitle('Build Tracker');

  const gridRef = useRef<AgGridReact<BuildRow> | null>(null);
  const gridApiRef = useRef<GridApi<BuildRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [conclusionFilter, setConclusionFilter] = useState<ConclusionFilter>(null);
  const [branchFilter, setBranchFilter] = useState<BranchFilter>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [detailRow, setDetailRow] = useState<BuildRow | null>(null);

  const hasFilters = Boolean(
    conclusionFilter || branchFilter || timeFilter !== 'all' || searchText.trim()
  );

  const clearFilters = useCallback((): void => {
    setConclusionFilter(null);
    setBranchFilter(null);
    setTimeFilter('all');
    setSearchText('');
  }, []);

  const stateRef = useRef<{
    searchText: string;
    conclusionFilter: ConclusionFilter;
    branchFilter: BranchFilter;
    timeFilter: TimeFilter;
  }>({ searchText, conclusionFilter, branchFilter, timeFilter });
  stateRef.current = { searchText, conclusionFilter, branchFilter, timeFilter };

  // ── Datasource — PostgREST direct on builds ────────────────────
  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, successCallback, failCallback } = params;
    const {
      searchText: st,
      conclusionFilter: cf,
      branchFilter: bf,
      timeFilter: tf,
    } = stateRef.current;

    try {
      let query = supabase
        .from('builds')
        .select('*', { count: 'exact' })
        .order('run_started_at', { ascending: false });

      if (cf) query = query.eq('conclusion', cf);
      if (bf) query = query.eq('head_branch', bf);

      const cutoff = timeFilterCutoff(tf);
      if (cutoff) query = query.gte('run_started_at', cutoff);

      if (st.trim()) {
        // buildSearchOrFilter wraps the term via the canonical escapeOrIlikeTerm
        // (quoted, lossless): commas/parens in a commit message are matched, not
        // silently stripped.
        query = query.or(buildSearchOrFilter(st));
      }

      // IGetRowsParams.endRow is exclusive; PostgREST .range is inclusive.
      const { data, error, count } = await query.range(startRow, endRow - 1);
      if (error) throw new Error(error.message);

      const rows = (data ?? []) as BuildRow[];
      const total = count ?? 0;
      setTotalCount(total);
      setLoaded(true);
      successCallback(rows, total);
    } catch (e) {
      reportDatasourceFailure(
        'BuildTracker',
        { rpcName: 'builds', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<BuildRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  // Refresh grid when filters change.
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, conclusionFilter, branchFilter, timeFilter, getRowsFn]);

  // ── Cell renderers ─────────────────────────────────────────────
  const ConclusionCell = useCallback((p: ICellRendererParams<BuildRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <ConclusionBadge conclusion={p.data.conclusion} />;
  }, []);

  const CommitCell = useCallback((p: ICellRendererParams<BuildRow>): JSX.Element | null => {
    if (!p.data) return null;
    return (
      <div className="flex items-center gap-2 min-w-0 w-full">
        <span className="font-mono text-xs text-ink-secondary flex-shrink-0">
          {p.data.commit_sha.slice(0, 7)}
        </span>
        <span className="text-xs text-ink-primary truncate">{p.data.commit_message ?? '—'}</span>
      </div>
    );
  }, []);

  const BranchCell = useCallback((p: ICellRendererParams<BuildRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <span className="text-xs text-ink-secondary">{p.data.head_branch ?? '—'}</span>;
  }, []);

  const ActorCell = useCallback((p: ICellRendererParams<BuildRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <span className="text-xs text-ink-secondary">{p.data.actor ?? '—'}</span>;
  }, []);

  const TimeCell = useCallback((p: ICellRendererParams<BuildRow>): JSX.Element | null => {
    if (!p.data) return null;
    return (
      <span className="font-mono text-xs text-ink-muted">{relativeTime(p.data.run_started_at)}</span>
    );
  }, []);

  const ViewRunCell = useCallback((p: ICellRendererParams<BuildRow>): JSX.Element | null => {
    if (!p.data?.html_url) return null;
    return (
      <a
        href={p.data.html_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-ink-muted hover:text-accent inline-flex items-center"
        title="View run on GitHub"
        aria-label="View run on GitHub"
      >
        <ExternalLink size={14} />
      </a>
    );
  }, []);

  const columnDefs = useMemo<ColDef<BuildRow>[]>(
    () =>
      [
        {
          headerName: 'Status',
          field: 'conclusion',
          width: 120,
          cellRenderer: ConclusionCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Commit',
          field: 'commit_message',
          flex: 1,
          minWidth: 300,
          cellRenderer: CommitCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
          tooltipField: 'commit_message',
        },
        {
          headerName: 'Branch',
          field: 'head_branch',
          width: 130,
          cellRenderer: BranchCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Actor',
          field: 'actor',
          width: 130,
          cellRenderer: ActorCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Started',
          field: 'run_started_at',
          width: 110,
          cellRenderer: TimeCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: '',
          colId: 'view_run',
          width: 56,
          maxWidth: 56,
          sortable: false,
          resizable: false,
          cellRenderer: ViewRunCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
      ] as ColDef<BuildRow>[],
    [ConclusionCell, CommitCell, BranchCell, ActorCell, TimeCell, ViewRunCell]
  );

  const defaultColDef = useMemo<ColDef<BuildRow>>(
    () => ({
      sortable: false,
      resizable: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerFontSize: '10px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  const onRowClicked = useCallback((event: RowClickedEvent<BuildRow>): void => {
    if (!event.data) return;
    const domEvent = event.event as Event | undefined;
    const target = domEvent?.target as HTMLElement | null;
    // Don't open the panel when the row's "View run" link (an <a>) is clicked.
    if (target?.closest('button') || target?.closest('a')) return;
    setDetailRow(event.data);
  }, []);

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Build Tracker</h1>
            <span className="text-xs text-ink-muted font-mono">
              {totalCount === 1 ? '1 build' : `${totalCount.toLocaleString()} builds`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <FilterGroup
            options={CONCLUSION_FILTERS}
            value={conclusionFilter}
            onChange={setConclusionFilter}
          />
          <FilterGroup options={BRANCH_FILTERS} value={branchFilter} onChange={setBranchFilter} />
          <FilterGroup options={TIME_FILTERS} value={timeFilter} onChange={setTimeFilter} />
          <div className="relative ml-auto">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search commit, branch, actor..."
              aria-label="Search builds"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
            />
          </div>
        </div>
      </div>

      {/* Grid + optional detail panel */}
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        <div className="flex-1 p-2 relative" style={{ minHeight: 0 }}>
          <AgGridReact
            ref={gridRef}
            theme={gridTheme}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="infinite"
            cacheBlockSize={200}
            maxBlocksInCache={50}
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
            animateRows={false}
            headerHeight={36}
            rowHeight={42}
            suppressCellFocus={true}
            suppressHorizontalScroll={true}
            suppressNoRowsOverlay={true}
          />

          {/* Empty states (TONE §4) — two distinct: first-time vs filter-empty.
              Gated on `loaded` so it never flashes before the first fetch. */}
          {loaded && totalCount === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              {hasFilters ? (
                <>
                  <p className="text-sm text-ink-secondary">No builds match your current filters</p>
                  <button onClick={clearFilters} className="arbi-btn-ghost text-xs mt-2">
                    Clear filters
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-ink-secondary">No builds recorded yet</p>
                  <p className="text-xs text-ink-muted mt-1 max-w-xs">
                    Builds appear here when CI completes a run on a connected branch.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {detailRow && <BuildDetailPanel row={detailRow} onClose={() => setDetailRow(null)} />}
      </div>
    </div>
  );
}
